import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Shield, ShieldCheck, ShieldAlert } from 'lucide-react';
import FneCertifyModal from './FneCertifyModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache module-level pour éviter de re-requêter à chaque mount
let _permsCache = null;
let _permsCachePromise = null;
async function fetchFnePermissions() {
  if (_permsCache) return _permsCache;
  if (_permsCachePromise) return _permsCachePromise;
  _permsCachePromise = axios.get(`${API}/fne/my-permissions`)
    .then(r => {
      _permsCache = r.data;
      return r.data;
    })
    .catch(() => ({ canCertify: false, canRefund: false, canView: false }))
    .finally(() => { _permsCachePromise = null; });
  return _permsCachePromise;
}

export function invalidateFnePermissions() { _permsCache = null; }

/**
 * Badge + bouton qui résume l'état FNE et permet la certification.
 * Si `canCertify` est non précisé, on récupère la permission via /api/fne/my-permissions.
 */
export default function FneCertifyButton({
  certification, targetType, targetId, targetLabel,
  onCertified, onSuccess, canCertify: canCertifyProp,
  clientFneEnabled, variant,
}) {
  const [open, setOpen] = useState(false);
  const [permCanCertify, setPermCanCertify] = useState(null);

  useEffect(() => {
    if (canCertifyProp !== undefined) return;
    let active = true;
    fetchFnePermissions().then(p => {
      if (active) setPermCanCertify(!!p.canCertify);
    });
    return () => { active = false; };
  }, [canCertifyProp]);

  const canCertify = canCertifyProp !== undefined ? canCertifyProp : permCanCertify;
  const status = certification?.status || 'not_certified';
  const handleSuccess = (c) => { onCertified?.(c); onSuccess?.(c); };

  // Variant "workflow-card" : grosse tuile dans l'onglet Devis & Facture
  if (variant === 'workflow-card') {
    const isCertified = status === 'certified' || status === 'refunded_partial' || status === 'refunded_total' || status === 'refunded';
    if (isCertified) {
      const pdfUrl = `${API}/fne/pdf/${targetType}/${targetId}`;
      return (
        <a
          href={pdfUrl} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 p-4 border-2 border-emerald-300 bg-emerald-50 rounded-lg hover:border-emerald-500 transition-colors"
          data-testid="generate-fne-invoice-workflow-btn"
        >
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          <span className="font-medium text-emerald-800">
            📄 PDF FNE certifié — {certification?.reference}
          </span>
        </a>
      );
    }
    const disabled = clientFneEnabled === false;
    return (
      <>
        <button
          onClick={() => !disabled && setOpen(true)}
          disabled={disabled}
          title={disabled ? "Activez la facturation FNE pour ce client (Informations Client → Activer)" : "Certifier cette facture auprès de la DGI"}
          className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg transition-colors ${
            disabled ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
            : 'border-emerald-400 hover:border-emerald-600 hover:bg-emerald-50'
          }`}
          data-testid="generate-fne-invoice-workflow-btn"
        >
          <Shield className={`w-5 h-5 ${disabled ? 'text-gray-400' : 'text-emerald-600'}`} />
          <span className={`font-medium ${disabled ? 'text-gray-400' : 'text-emerald-800'}`}>
            {disabled ? 'Facturation FNE désactivée' : 'Générer Facture FNE'}
          </span>
          {!disabled && (
            <Badge className="bg-emerald-600 text-white text-[10px]">Certifié DGI</Badge>
          )}
        </button>
        <FneCertifyModal
          open={open}
          onClose={() => setOpen(false)}
          onSuccess={handleSuccess}
          targetType={targetType}
          targetId={targetId}
          targetLabel={targetLabel}
        />
      </>
    );
  }

  // === Sprint 2 — Statuts étendus
  if (status === 'pending_fne') {
    return (
      <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50" data-testid="fne-status-pending">
        <Shield className="h-3 w-3 mr-1 animate-pulse" />
        Certification en cours...
      </Badge>
    );
  }

  if (status === 'certified') {
    const pdfUrl = `${API}/fne/pdf/${targetType}/${targetId}`;
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="fne-status-certified">
        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
          <ShieldCheck className="h-3 w-3 mr-1" />
          FNE — {certification?.reference}
        </Badge>
        <Badge variant="outline" className="border-slate-400 text-slate-600 text-[10px]">
          🔒 Verrouillée
        </Badge>
        {certification?.token && (
          <a
            href={certification.token}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-emerald-700 underline hover:text-emerald-900"
            data-testid="fne-verify-link"
          >
            Vérifier DGI
          </a>
        )}
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-blue-700 underline hover:text-blue-900"
          data-testid="fne-pdf-link"
        >
          📄 PDF certifié
        </a>
      </div>
    );
  }

  if (status === 'refunded' || status === 'refunded_total') {
    return (
      <Badge variant="outline" className="border-slate-400 text-slate-600" data-testid="fne-status-refunded-total">
        <ShieldAlert className="h-3 w-3 mr-1" />
        FNE annulée (avoir total {certification?.refund?.reference || ''})
      </Badge>
    );
  }

  if (status === 'refunded_partial') {
    const pdfUrl = `${API}/fne/pdf/${targetType}/${targetId}`;
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="fne-status-refunded-partial">
        <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
          <ShieldAlert className="h-3 w-3 mr-1" />
          Avoir partiel émis — {certification?.reference}
        </Badge>
        <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
          className="text-xs text-blue-700 underline hover:text-blue-900" data-testid="fne-pdf-link-partial">
          📄 PDF certifié
        </a>
      </div>
    );
  }

  if (status === 'failed' || status === 'fne_error') {
    return (
      <div className="flex items-center gap-2" data-testid="fne-status-failed">
        <Badge variant="destructive">
          <ShieldAlert className="h-3 w-3 mr-1" />
          Échec FNE ({certification?.errorCode})
        </Badge>
        {canCertify && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(true)}
              data-testid="fne-retry-btn"
            >
              Réessayer
            </Button>
            <FneCertifyModal
              open={open}
              onClose={() => setOpen(false)}
              onSuccess={(c) => onCertified?.(c)}
              targetType={targetType}
              targetId={targetId}
              targetLabel={targetLabel}
            />
          </>
        )}
      </div>
    );
  }

  // not_certified ou pending
  return (
    <div className="flex items-center gap-2" data-testid="fne-status-not-certified">
      <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50">
        <Shield className="h-3 w-3 mr-1" />
        Non certifiée FNE
      </Badge>
      {canCertify && (
        <>
          <Button
            size="sm"
            onClick={() => setOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="fne-certify-btn"
          >
            <Shield className="h-3.5 w-3.5 mr-1.5" />
            Certifier FNE
          </Button>
          <FneCertifyModal
            open={open}
            onClose={() => setOpen(false)}
            onSuccess={(c) => onCertified?.(c)}
            targetType={targetType}
            targetId={targetId}
            targetLabel={targetLabel}
          />
        </>
      )}
    </div>
  );
}
